import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Heart, TreePine, Users, GraduationCap, ArrowRight, CheckCircle, AlertTriangle } from 'lucide-react';
import { useSocialEngagementNotifications } from '@/hooks/useSocialEngagementNotifications';
import { useNavigate } from 'react-router-dom';

interface SocialEngagementWidgetProps {
  tenantId: string;
}

export function SocialEngagementWidget({ tenantId }: SocialEngagementWidgetProps) {
  const { thresholds, loading, reachedCount, inactiveCount } = useSocialEngagementNotifications();
  const navigate = useNavigate();

  const getProjectIcon = (type: string) => {
    switch (type) {
      case 'trees': return TreePine;
      case 'families': return Heart;
      case 'education': return GraduationCap;
      default: return Heart;
    }
  };

  const getProjectTypeName = (type: string) => {
    switch (type) {
      case 'trees': return 'Bäume';
      case 'families': return 'Familien';
      case 'education': return 'Bildung';
      default: return 'Projekt';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Heart className="h-5 w-5 text-red-500" />
            Soziales Engagement
          </CardTitle>
          <CardDescription>Lade Engagement-Status...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4 text-muted-foreground">
            Lädt...
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Heart className="h-5 w-5 text-red-500" />
          Soziales Engagement
          {reachedCount > 0 && (
            <Badge variant="default">
              {reachedCount}
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          Übersicht über soziale Projekte und Meilensteine
        </CardDescription>
      </CardHeader>
      <CardContent>
        {thresholds.length === 0 ? (
          <div className="text-center py-6">
            <Heart className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground mb-4">
              Keine sozialen Projekte aktiviert
            </p>
            <Button 
              variant="outline" 
              onClick={() => navigate('/admin/soziales-engagement')}
            >
              Projekte aktivieren
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Summary Stats */}
            {reachedCount > 0 && (
              <div className="text-center p-3 bg-muted rounded-lg">
                <div className="text-2xl font-bold text-green-600">{reachedCount}</div>
                <div className="text-xs text-muted-foreground">Meilensteine erreicht</div>
              </div>
            )}

            {/* Project List */}
            <div className="space-y-3">
              {thresholds.slice(0, 3).map((project) => {
                const Icon = getProjectIcon(project.type);
                const progress = Math.min((project.current_contributions / project.threshold) * 100, 100);
                
                return (
                  <div key={project.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <Icon className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <div className="font-medium text-sm">{project.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {project.current_contributions}/{project.threshold} {getProjectTypeName(project.type)}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {project.is_threshold_reached ? (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      ) : (
                        <div className="w-12 bg-muted rounded-full h-2">
                          <div 
                            className="bg-primary h-2 rounded-full transition-all duration-300"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Action Button */}
            <Button 
              variant="outline" 
              className="w-full" 
              onClick={() => navigate('/admin/soziales-engagement')}
            >
              Alle Projekte verwalten
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}